import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface FilterChip {
  id: string;
  label: string;
  value: string;
  color?: 'blue' | 'purple' | 'orange' | 'teal' | 'cyan' | 'red' | 'green' | 'gray';
}

interface ActiveFilterChipsProps {
  chips: FilterChip[];
  onRemove: (chipId: string) => void;
  onClearAll: () => void;
  onSaveAsDefault?: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
}

const colorClasses: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-300 hover:bg-teal-200',
  cyan: 'bg-cyan-100 text-cyan-800 border-cyan-300 hover:bg-cyan-200',
  red: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200',
  green: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200',
  gray: 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200',
};

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  chips,
  onRemove,
  onClearAll,
  onSaveAsDefault,
  isSaving = false,
  hasUnsavedChanges = false,
}) => {
  if (chips.length === 0) return null;

  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap flex-1">
        <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Active Filters:</span>
        {chips.map((chip) => (
          <span
            key={chip.id}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
              colorClasses[chip.color || 'blue']
            }`}
          >
            <span className="font-semibold">{chip.label}:</span>
            <span className={chip.id === 'timeRange' ? 'whitespace-nowrap' : 'truncate max-w-[150px]'} title={chip.value}>{chip.value}</span>
            <button
              type="button"
              onClick={() => onRemove(chip.id)}
              className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 transition-colors"
              title={`Clear ${chip.label} filter`}
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {onSaveAsDefault && (
          <button
            type="button"
            onClick={onSaveAsDefault}
            disabled={isSaving || !hasUnsavedChanges}
            className={`text-xs font-medium transition-colors ${
              hasUnsavedChanges
                ? 'text-green-600 hover:text-green-800'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            title={hasUnsavedChanges ? 'Save current filters as default' : 'No changes to save'}
          >
            {isSaving ? 'Saving...' : 'Save as Default'}
          </button>
        )}
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

export default ActiveFilterChips;
